/**
 * Print-only page rendered at `/print/report/:token`.
 *
 * This page is loaded by the backend Playwright renderer (and nothing else).
 * It is a pure output form — the PDF equivalent of the Executive Summary
 * Report view — and MUST reuse the exact same components as the live
 * review tab so the PDF matches what the consultant sees on screen.
 *
 * Contract:
 *   - Data source:   finalized `version.snapshot` ONLY (no live recompute).
 *   - Components:    real `<ReviewReportTab>` + `<ClientInputsSummary>` from
 *                    `AssessmentDetailPage.jsx` — zero duplication.
 *   - App chrome:    NONE. `isViewingVersion` + `isFinalized` are forced
 *                    true so every action bar / finalize button is elided.
 *   - Readiness:     `<div data-print-ready="true">` flipped once data +
 *                    fonts + a paint cycle have resolved; Playwright waits
 *                    on it before calling `page.pdf()`.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ReviewReportTab, ClientInputsSummary } from "../consultant/AssessmentDetailPage";
import "./PrintReport.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function PrintReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/admin/print/data/${token}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Failed to load report data");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Flip `data-print-ready` once data is loaded, fonts are ready, and React
  // has had a couple of paint frames to settle layout.
  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    (async () => {
      try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch { /* noop */ }
      // Two paint cycles + small buffer so any child effect-driven layout
      // (lazy tables, grids) has a chance to commit before the PDF snapshot.
      await new Promise((r) => requestAnimationFrame(() => r()));
      await new Promise((r) => setTimeout(r, 600));
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [data]);

  // Compose the `assessment` prop exactly the way the consultant page does
  // when viewing a finalized version (see `displayAssessment` in
  // AssessmentDetailPage.jsx): merge the snapshot's frozen assessment fields
  // with the client_inputs so every field the report components read is
  // present.
  const composedAssessment = useMemo(() => {
    if (!data) return null;
    const snap = data.version?.snapshot || {};
    return {
      ...(snap.executive_summary?.assessment || {}),
      ...(snap.client_inputs || {}),
    };
  }, [data]);

  if (error) return <div className="print-error">Unable to load report: {error}</div>;
  if (!data) return <div className="print-loading">Loading report…</div>;

  const snapshot = data.version?.snapshot || {};
  const versionNumber = data.version?.version_number || 1;
  const questions = data.questions || [];
  const companyName = composedAssessment?.company_name || "Client";
  // Branding strings — editable at Admin → Branding. Defaults are used only
  // if the admin hasn't touched the respective field yet.
  const branding = data.branding || {};
  const footerFirm = branding.footer_text || "PrimeS4 Consulting";
  const footerNotice = branding.report_confidentiality_notice || "Confidential · for client discussion only";
  const platformLogo = branding.partner_logo || branding.platform_logo || "";
  // Client logo — uploaded per-assessment from Version History Drawer, lives
  // on the live assessment document (`client_logo_base64` + `client_logo_mime`).
  // We pull from `data.assessment` (the top-level token payload) rather than
  // the frozen snapshot so consultants can swap a logo without re-finalising.
  const liveAssessment = data.assessment || {};
  const clientLogoDataUrl = liveAssessment.client_logo_base64
    ? `data:${liveAssessment.client_logo_mime || "image/png"};base64,${liveAssessment.client_logo_base64}`
    : "";

  return (
    <div className="print-root" data-print-ready={ready ? "true" : undefined}>
      {/* Branded cover block — left: platform logo (from Admin → Branding);
          right: client logo (from Version History Drawer). Either side may
          be blank when not uploaded; version number lives in the footer. */}
      <header className="print-brand">
        <div className="print-brand-left">
          {platformLogo && (
            <img loading="lazy"
              src={platformLogo}
              alt="Platform logo"
              className="print-brand-logo"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
        </div>
        <div className="print-brand-right">
          {clientLogoDataUrl && (
            <img loading="lazy"
              src={clientLogoDataUrl}
              alt={`${companyName} logo`}
              className="print-client-logo"
            />
          )}
        </div>
      </header>

      {/* -------------------------------------------------------------
          The real Executive Summary Report — identical to the consultant
          view. Passing `isFinalized` + `isViewingVersion` + no
          `onFinalize` hides every action bar.
          ------------------------------------------------------------- */}
      <main className="print-body">
        <ReviewReportTab
          assessment={composedAssessment}
          assessmentId={undefined}
          isFinalized
          isViewingVersion
          snapshot={snapshot}
        />

        {/* Appendix A — reuses `ClientInputsSummary` so the answer layout
            exactly matches the Client Inputs tab. Question catalogue is
            piped in from the token data so the component never calls the
            admin-only endpoint. */}
        <section className="print-appendix">
          <h3 className="print-appendix-heading">Appendix A · Client Inputs</h3>
          <ClientInputsSummary assessment={composedAssessment} questions={questions} />
        </section>
      </main>

      <footer className="print-footer">
        <div className="print-footer-left">Prepared for {companyName} · {footerFirm}</div>
        <div className="print-footer-center" data-testid="print-footer-version">v{versionNumber}</div>
        <div className="print-footer-right print-footer-muted">{footerNotice} · {new Date().getFullYear()}</div>
      </footer>
    </div>
  );
}
